import { Button, FormControl, Grid2, InputLabel, MenuItem, Pagination, PaginationItem, Select, Typography } from "@mui/material";
import { Fragment, useEffect, useState } from "react";

import { Link, useSearchParams } from "react-router-dom";
import { ArrowBackOutlined, ArrowForwardOutlined } from "@mui/icons-material";
import GeneralItem from "./commons/GeneralItem";
import {
    fetchList,
    isAllCategories,
    materialCategoryName,
    materialImage,
    newsImage,
    pagesFor,
    resultsLabel,
} from "../utils/api";

const dates = [
    { id: "", name: "Em qualquer data" },
    { id: "1", name: "Últimas 24 horas" },
    { id: "7", name: "Na última semana" },
    { id: "30", name: "No último mês" },
    { id: "365", name: "No último ano" },
];

const TYPES = [
    { id: "all", label: "Todos" },
    { id: "news", label: "Notícias" },
    { id: "gallery", label: "Galeria" },
    { id: "materials", label: "Materiais" },
];

const LIMIT = 20;

// data usada para ordenar resultados de fontes diferentes
const sortDate = (item) => new Date(item.incData || item.data || item.createdAt || 0).getTime();

function Search({ isMobile }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(null);
    const [maxPages, setMaxPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const page = Number(searchParams.get("page")) || 1;
    const search = searchParams.get("search") || "";
    const categorie = searchParams.get("categorie") || "";
    const since = searchParams.get("since") || "";
    const typeParam = searchParams.get("type") || "all";
    const type = TYPES.some((t) => t.id === typeParam) ? typeParam : "all";

    const activeCategorie = isAllCategories(categorie) ? "" : categorie;
    // categoria numerica = categoria de materiais; texto = categoria de noticias
    const isMaterialCategorie = activeCategorie !== "" && !Number.isNaN(Number(activeCategorie));

    const wantsNews = (type === "all" || type === "news") && !(isMaterialCategorie);
    const wantsMaterials = (type === "all" || type === "materials") && !(activeCategorie && !isMaterialCategorie);
    const wantsGallery = (type === "all" || type === "gallery") && activeCategorie === "";

    function updateParams(changes) {
        const next = {};
        const merged = { search, categorie, since, type, page: 1, ...changes };

        if (merged.search) next.search = merged.search;
        if (merged.categorie && !isAllCategories(merged.categorie)) next.categorie = merged.categorie;
        if (merged.since) next.since = merged.since;
        if (merged.type && merged.type !== "all") next.type = merged.type;
        if (merged.page && merged.page > 1) next.page = merged.page;

        setSearchParams(next);
    }

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);

        const common = { title: search, limit: LIMIT, page: page - 1, since };
        const requests = [];

        if (wantsNews) {
            requests.push(
                fetchList("news", { ...common, categorie: activeCategorie, slim: 1 }).then((r) => ({
                    ...r,
                    items: r.items.map((item) => ({ ...item, typeSearch: "news" })),
                }))
            );
        }

        if (wantsMaterials) {
            requests.push(
                fetchList("materials", { ...common, categorie: activeCategorie }).then((r) => ({
                    ...r,
                    items: r.items.map((item) => ({ ...item, typeSearch: "materials" })),
                }))
            );
        }

        if (wantsGallery) {
            requests.push(
                fetchList("gallery", common).then((r) => ({
                    ...r,
                    items: r.items.map((item) => ({ ...item, typeSearch: "gallery" })),
                }))
            );
        }

        Promise.allSettled(requests).then((results) => {
            if (cancelled) return;

            const fulfilled = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
            const failed = results.length > 0 && fulfilled.length === 0;

            const items = fulfilled
                .flatMap((r) => r.items)
                .sort((a, b) => sortDate(b) - sortDate(a));

            setData(items);

            // total so e conhecido quando todas as fontes enviam X-Total-Count
            const knowsTotal = fulfilled.length > 0 && fulfilled.every((r) => r.total !== null);
            setTotal(knowsTotal ? fulfilled.reduce((sum, r) => sum + r.total, 0) : null);
            setMaxPages(
                fulfilled.length
                    ? Math.max(
                        ...fulfilled.map((r) =>
                            pagesFor({ total: r.total, itemsLength: r.items.length, page, limit: LIMIT })
                        )
                    )
                    : 1
            );
            setError(failed);
            setLoading(false);
        });

        window.scrollTo({ top: 0, behavior: "smooth" });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search, activeCategorie, since, type]);

    function handlePageChange(_, number) {
        updateParams({ page: number });
    }

    const filterDescription = [
        search ? `"${search}"` : "",
        activeCategorie ? (isMaterialCategorie ? materialCategoryName(activeCategorie) : activeCategorie) : "",
        since ? (dates.find((d) => d.id === since) || {}).name?.toLowerCase() : "",
    ]
        .filter(Boolean)
        .join(" · ");

    const subtitle = (() => {
        if (loading) return "Carregando...";
        if (error) return "Não foi possível realizar a busca. Tente novamente em instantes.";
        if (total === null) return filterDescription ? `Resultados para ${filterDescription}` : "";
        const label = resultsLabel(total, "resultado", "resultados");
        return filterDescription ? `${label} para ${filterDescription}` : label;
    })();

    const tabStyle = (active) => ({
        color: active ? "#C81E1E" : "#010101",
        borderBottom: 1,
        fontFamily: "Raleway",
        fontStyle: "normal",
        fontWeight: "800",
        fontSize: isMobile ? "16px" : "20px",
        lineHeight: "23px",
        textTransform: "none",
        minWidth: "auto",
        padding: "0 4px",
        borderRadius: 0,
    });

    return (
        <Fragment>
            <Grid2 container marginY={6} gap={2} display={"flex"} paddingX={isMobile ? 2 : 8}>
                <Grid2 xs={12} md={8} style={{ color: "#010101", display: "flex", gap: 16, flexWrap: "wrap" }} role="tablist" aria-label="Tipo de conteúdo">
                    {TYPES.map((t) => (
                        <Button
                            key={t.id}
                            variant="text"
                            role="tab"
                            aria-selected={type === t.id}
                            onClick={() => updateParams({ type: t.id })}
                            sx={tabStyle(type === t.id)}
                        >
                            {t.label}
                        </Button>
                    ))}
                </Grid2>
                <Grid2 xs={12} md={3}>
                    <FormControl variant="standard" size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="date-filter-select-label">Filtrar por data</InputLabel>
                        <Select
                            id="date-filter-select"
                            labelId="date-filter-select-label"
                            value={since}
                            label="Filtrar por data"
                            displayEmpty
                            style={{
                                color: "#010101",
                                fontFamily: "Raleway",
                                fontStyle: "normal",
                                fontWeight: "500",
                                fontSize: "16px",
                                lineHeight: "23px",
                                textDecoration: "none",
                            }}
                            onChange={(e) => updateParams({ since: e.target.value })}
                        >
                            {dates.map((d) => (
                                <MenuItem key={d.id || "any"} value={d.id}>{d.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid2>

                <Grid2 xs={12} md={12}>
                    <Typography
                        role="status"
                        aria-live="polite"
                        color="#4A4A4A"
                        fontFamily="Raleway"
                        fontSize="14px"
                        lineHeight="20px"
                    >
                        {subtitle}
                    </Typography>
                </Grid2>

                {!loading && !error && data.length === 0 && (
                    <Grid2 xs={12} md={12}>
                        <Typography color="#010101" fontFamily="Raleway" fontSize="16px" lineHeight="22px">
                            Nenhum resultado encontrado{filterDescription ? ` para ${filterDescription}` : ""}.
                            {" "}Tente outras palavras, remova o filtro de data ou{" "}
                            <Link to="/busca-avancada" style={{ color: "#C81E1E" }}>limpe todos os filtros</Link>.
                        </Typography>
                    </Grid2>
                )}

                <Grid2 xs={12} md={12} display={"flex"} padding={"0px 10px"}>
                    <Grid2 container marginY={6} gap={4} display={"flex"}>
                        {data.map((item) => (
                            <Grid2
                                xs={12}
                                md={2.4}
                                xl={2.6}
                                display={"flex"}
                                padding={"0px 10px"}
                                key={`${item.typeSearch}-${item._id}`}
                            >
                                <GeneralItem
                                    image={
                                        item.typeSearch === "gallery"
                                            ? (item.photos?.[0]?.url || `https://galeria-images.s3.sa-east-1.amazonaws.com/${item.ID}/galeria/0_max.jpeg`)
                                            : item.typeSearch === "materials"
                                                ? materialImage(item)
                                                : newsImage(item)
                                    }
                                    date={item.incData || item.data || item.createdAt}
                                    description={item.titulo}
                                    link={
                                        item.typeSearch === "gallery"
                                            ? `/galeria/fotos?link=${item.link}`
                                            : item.typeSearch === "materials"
                                                ? `/materiais/item?link=${item.link}`
                                                : `/noticia?link=${item.link}`
                                    }
                                    categorie={item.categorie}
                                    categorieColor={item.categorieColor}
                                    isMobile={isMobile}
                                />
                            </Grid2>
                        ))}
                    </Grid2>
                </Grid2>
            </Grid2>

            {maxPages > 1 && (
                <Grid2
                    xs={12}
                    md={12}
                    padding={"16px 0px"}
                    color="#FFFFFF"
                    borderBottom={1}
                    display={"flex"}
                    justifyContent={"center"}
                >
                    <Pagination
                        color="warning"
                        variant="outlined"
                        count={maxPages}
                        page={Math.min(page, maxPages)}
                        onChange={handlePageChange}
                        showFirstButton
                        showLastButton
                        siblingCount={isMobile ? 0 : 1}
                        renderItem={(item) => (
                            <PaginationItem
                                style={{
                                    borderRadius: 100,
                                    color: "#C81E1E",
                                    padding: "8px 16px",
                                    fontFamily: "Raleway",
                                    fontStyle: "normal",
                                    fontWeight: "600",
                                    fontSize: "16px",
                                    lineHeight: "20px",
                                }}
                                slots={{ previous: ArrowBackOutlined, next: ArrowForwardOutlined }}
                                {...item}
                            />
                        )}
                    />
                </Grid2>
            )}
        </Fragment>
    );
}

export default Search;
